import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Chip, Alert, CircularProgress,
  Grid, IconButton, Tooltip, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import {
  Refresh, PlayArrow, Schedule, CheckCircle, Error as ErrorIcon,
  Warning as WarningIcon, WorkOutline, Info
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { GlassmorphicCard, BodyText, SecondaryText } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: `${theme.custom?.cardRounding || 28}px`,
  backdropFilter: `blur(${theme.custom?.glassBlur || 16}px)`,
  border: '1px solid rgba(255, 255, 255, 0.06)',
  '& .MuiTableHead-root': {
    '& .MuiTableCell-head': {
      background: `${theme.palette.primary.main}1a`,
      color: theme.palette.primary.main,
      fontWeight: 700,
      fontSize: '0.875rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.02)',
      },
      '& .MuiTableCell-body': {
        color: '#ffffff',
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        fontSize: '0.875rem',
      },
    },
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status'
})(({ status }) => {
  let bgColor, textColor, borderColor;

  const statusStr = String(status || 'unknown').toLowerCase();

  switch (statusStr) {
    case 'active':
    case 'running':
    case 'true':
      bgColor = 'rgba(0, 255, 136, 0.1)';
      textColor = '#00ff88';
      borderColor = 'rgba(0, 255, 136, 0.3)';
      break;
    case 'idle':
    case 'waiting':
      bgColor = 'rgba(0, 212, 255, 0.1)';
      textColor = '#00d4ff';
      borderColor = 'rgba(0, 212, 255, 0.3)';
      break;
    case 'failed':
    case 'error':
      bgColor = 'rgba(255, 68, 68, 0.1)';
      textColor = '#ff4444';
      borderColor = 'rgba(255, 68, 68, 0.3)';
      break;
    case 'paused':
    case 'disabled':
      bgColor = 'rgba(255, 193, 7, 0.1)';
      textColor = '#ffc107';
      borderColor = 'rgba(255, 193, 7, 0.3)';
      break;
    default:
      bgColor = 'rgba(158, 158, 158, 0.1)';
      textColor = '#9e9e9e';
      borderColor = 'rgba(158, 158, 158, 0.3)';
  }

  return {
    background: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    fontWeight: 500,
  };
});

const RunButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.3}px`,
  fontSize: '0.75rem',
  padding: '4px 12px',
  minWidth: 'unset',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
}));

const ImmichJobs = ({ apiBaseUrl, token }) => {
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runningJob, setRunningJob] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, jobId: null, jobName: '' });
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/immich/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJobs(response.data || {});
    } catch (err) {
      console.error('Error fetching Immich jobs:', err);
      setError(err.response?.data?.detail || 'Failed to fetch Immich jobs');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchJobs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchJobs, 30000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const handleRunJob = async (jobId) => {
    setRunningJob(jobId);
    setError(null);
    setSuccessMessage(null);
    try {
      await axios.post(`${apiBaseUrl}/api/immich/jobs/${jobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage(`Job "${jobId}" triggered successfully`);
      // Refresh jobs after a short delay
      setTimeout(fetchJobs, 2000);
    } catch (err) {
      console.error('Error running Immich job:', err);
      setError(err.response?.data?.detail || `Failed to run job ${jobId}`);
    } finally {
      setRunningJob(null);
      setConfirmDialog({ open: false, jobId: null, jobName: '' });
    }
  };

  const openConfirmDialog = (jobId, jobName) => {
    setConfirmDialog({ open: true, jobId, jobName });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, jobId: null, jobName: '' });
  };

  const formatJobName = (jobId) => {
    // Convert snake_case or camelCase to readable format
    return jobId
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getJobStatus = (jobData) => {
    if (!jobData) return 'unknown';
    if (jobData.isActive) return 'active';
    if (jobData.isPaused) return 'paused';
    return 'idle';
  };

  const getJobStatusLabel = (jobData) => {
    const status = getJobStatus(jobData);
    if (status === 'active') return 'Running';
    if (status === 'paused') return 'Paused';
    return 'Idle';
  };

  // Convert jobs object to array for easier rendering
  const jobsArray = Object.entries(jobs).map(([id, data]) => ({
    id,
    ...data,
  }));

  if (loading && jobsArray.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600 }}>
          Immich Jobs
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchJobs} disabled={loading} sx={{ color: '#00d4ff' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {jobsArray.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          py: 6,
          color: 'rgba(255, 255, 255, 0.7)'
        }}>
          <Typography variant="body1">
            {loading ? 'Loading jobs...' : 'No jobs found'}
          </Typography>
        </Box>
      ) : (
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Queue</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobsArray.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkOutline sx={{ color: '#00d4ff', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500 }}>
                          {formatJobName(job.id)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                          {job.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={getJobStatusLabel(job)}
                      status={getJobStatus(job)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      {job.queueCount !== undefined ? `${job.queueCount} items` : 'N/A'}
                    </Typography>
                    {job.queueSize !== undefined && job.queueSize > 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                        {job.queueSize} bytes
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <RunButton
                      variant="contained"
                      size="small"
                      startIcon={<PlayArrow />}
                      onClick={() => openConfirmDialog(job.id, formatJobName(job.id))}
                      disabled={runningJob === job.id || job.isActive}
                      sx={{
                        backgroundColor: (theme) => job.isActive 
                          ? 'rgba(158, 158, 158, 0.2)' 
                          : theme.palette.secondary.main,
                        color: (theme) => theme.palette.secondary.contrastText,
                        '&:hover': {
                          backgroundColor: (theme) => job.isActive 
                            ? 'rgba(158, 158, 158, 0.2)' 
                            : theme.palette.secondary.main,
                          filter: 'brightness(1.1)'
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(255,255,255,0.5)',
                        }
                      }}
                    >
                      {runningJob === job.id ? (
                        <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                      ) : job.isActive ? (
                        'Running'
                      ) : (
                        'Run'
                      )}
                    </RunButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{
          sx: {
            background: (theme) => theme.palette.background.paper,
            backdropFilter: (theme) => `blur(${theme.custom?.glassBlur || 20}px)`,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: (theme) => `${theme.custom?.cardRounding || 28}px`,
          }
        }}
      >
        <DialogTitle sx={{ color: '#ffffff' }}>
          Confirm Run Job
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Are you sure you want to run the job "{confirmDialog.jobName}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={closeConfirmDialog}
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': { color: '#ffffff' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleRunJob(confirmDialog.jobId)}
            variant="contained"
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              color: (theme) => theme.palette.secondary.contrastText,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.secondary.main,
                filter: 'brightness(1.1)'
              }
            }}
          >
            Run Job
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImmichJobs;
